import { MDXProvider } from "@mdx-js/react";
import { graphql, navigate } from "gatsby";
import Img from "gatsby-image";
import { MDXRenderer } from "gatsby-plugin-mdx";
import {
  CodePen,
  Instagram,
  Pin,
  Spotify,
  Tweet,
  Vimeo,
  YouTube,
} from "mdx-embed";
import Bookmark from "../components/MDX-components/Bookmark";
import React, { useEffect, useState } from "react";
import Gist from "react-gist";
import CopyLink from "../components/copy-link";
import CtaMini from "../components/CtaMini";
import PageMeta from "../components/draftbox-meta/PageMeta";
import Layout from "../components/Layout";
import Code from "../components/MDX-components/Code";
import HtmlSnippet from "../components/MDX-components/HTMLSnippet";
import Image from "../components/MDX-components/Image";
import ImageGallery from "../components/MDX-components/ImageGallery";
import Notice from "../components/MDX-components/Notice";
import CustomIframe from "../components/MDX-components/CustomIframe";
import { DraftboxPage } from "../models/draftbox-page-description.model";
import "../styles/prism.css";
import "../styles/richtext.css";
import EmbedWithCaptionHOC from "../components/MDX-components/EmbedWithCaptionHOC";
import Zoom from "react-medium-image-zoom";
import CtaPost from "../components/CtaPost";
import Company from "../components/Company";
import AnchorLink from "../components/MDX-components/AnchorLink";

const components = {
  Tweet: EmbedWithCaptionHOC(Tweet),
  CodePen: EmbedWithCaptionHOC(CodePen),
  Instagram: EmbedWithCaptionHOC(Instagram),
  YouTube: EmbedWithCaptionHOC(YouTube),
  Vimeo: EmbedWithCaptionHOC(Vimeo),
  Spotify: EmbedWithCaptionHOC(Spotify),
  Pin: EmbedWithCaptionHOC(Pin),
  HtmlSnippet,
  Notice,
  ImageGallery,
  Image,
  code: Code.Prism,
  pre: Code.Pre,
  Gist: EmbedWithCaptionHOC(Gist),
  Bookmark,
  CustomIframe,
  a: null,
};

type IndexPageProps = {
  data: {
    draftboxPage: DraftboxPage;
    draftboxSettings: {
      content: {
        cta: {
          showOnPage: boolean;
        };
        subscribe: {
          showOnPage: boolean;
        };
        misc: {
          share: string;
        }
      };
    };
    site: {
      siteMetadata: {
        pathPrefix: string;
      };
    };
  };
  location: any;
};

const PageTemplate: React.FC<IndexPageProps> = ({ data, location }) => {
  const { draftboxPage, draftboxSettings, site } = data;

  components.a = AnchorLink({ subdir: site.siteMetadata.pathPrefix });

  const [href, sethref] = useState("");

  const [origin, setOrigin] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      sethref(window.location.href);
      setOrigin(window.location.origin);
    }
  }, []);

  const twitterShareUrl = `https://twitter.com/share?text=${draftboxPage.title}&url=${href}`;

  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${href}`;

  const linkedInShareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${href}&title=${draftboxPage.title}`;

  const mailShareUrl = `mailto:?subject=${draftboxPage.title}&body=${href}`;

  let pinterestShareUrl = `https://www.pinterest.com/pin/create/button/?url=${href}&description=${draftboxPage.title}`;
  if (draftboxPage.featureImage && draftboxPage.featureImage.publicURL) {
    pinterestShareUrl += `&media=${
      origin + draftboxPage.featureImage.publicURL
    }`;
  }

  const whatsAppShareUrl = `https://wa.me/?text=${encodeURIComponent(
    draftboxPage.title + "\n" + href
  )}`;

  const handleNavigation = (e: any, slug) => {
    e.stopPropagation();
    navigate(slug);
  };

  return (
    <Layout>
      <PageMeta page={draftboxPage} />
      <div className="spacer my-8 lg:my-12"></div>
      <section className="px-6 max-w-3xl mx-auto">
        <h1
          dangerouslySetInnerHTML={{ __html: draftboxPage.title }}
          className="dangerous-html text-4xl lg:text-5xl font-sansBold break-words leading-tight"
        ></h1>
      </section>
      <div className="spacer my-8 lg:my-12"></div>

      {draftboxPage.featureImage?.childImageSharp && (
        <section className="mx-auto w-full max-w-4xl">
          <Zoom wrapStyle={{ width: "100%" }}>
            <Img
              className="w-full"
              style={{ maxHeight: "100%", maxWidth: "100%" }}
              fluid={draftboxPage.featureImage.childImageSharp.fluid as any}
              alt=""
            />
          </Zoom>
        </section>
      )}

      {draftboxPage.featureImage &&
        !draftboxPage.featureImage?.childImageSharp && (
          <section className="mx-auto w-full max-w-4xl">
            <Zoom wrapStyle={{ width: "100%" }}>
              <img
                style={{ maxHeight: "100%" }}
                className="mx-auto w-full"
                src={draftboxPage.featureImage?.publicURL}
                alt={draftboxPage.title}
              />
            </Zoom>
          </section>
        )}

      {draftboxPage.featureImage && (
        <div className="spacer my-8 lg:my-12"></div>
      )}

      <div className="prose prose-lg lg:prose-xl max-w-3xl px-6 mx-auto font-serifNormal text-gray-900">
        <MDXProvider components={components}>
          <MDXRenderer>{draftboxPage.markdownBody}</MDXRenderer>
        </MDXProvider>
      </div>

      <div className="spacer my-12 lg:my-16"></div>

      <div className="flex items-center max-w-3xl mt-8 mx-auto px-6">
        <span className="mr-2 text-lg text-gray-700">{draftboxSettings.content.misc.share}:</span>
        <div className="social-icons">
          <ul className="flex">
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={facebookShareUrl}
                aria-label="share on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 p-2 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={twitterShareUrl}
                aria-label="share on Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-twitter fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 p-2 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={linkedInShareUrl}
                aria-label="share on Linkedin"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-linkedin-in fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <span
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2 cursor-pointer"
                onClick={(e) => window.open(pinterestShareUrl, "_blank")}
                aria-label="share on Pinterest"
              >
                <i className="fab fa-pinterest-p fa-fw text-white"></i>
              </span>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={whatsAppShareUrl}
                aria-label="share on Whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-whatsapp fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <a
                className="flex justify-center items-center w-8 h-8 bg-gray-700 hover:bg-primary rounded-full mr-2"
                href={mailShareUrl}
                aria-label="share on Mail"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-envelope fa-fw text-white"></i>
              </a>
            </li>
            <li>
              <CopyLink textToCopy={href} />
            </li>
          </ul>
        </div>
      </div>

      <div className="spacer my-8 lg:my-12"></div>
      {draftboxSettings.content.subscribe.showOnPage && <CtaMini />}
      {draftboxSettings.content.cta.showOnPage && (
        <Company size="large" className="my-16 container mx-auto" />
      )}
    </Layout>
  );
};

export default PageTemplate;

export const PageTemplateQuery = graphql`
  query($slug: String!) {
    draftboxPage(slug: { eq: $slug }) {
      ...DraftboxPageDetails
    }
    site {
      siteMetadata {
        pathPrefix
      }
    }
    draftboxSettings {
      content {
        cta {
          showAfterBlogfeed
          showOnPage
        }
        subscribe {
          showOnPage
        }
        misc {
          share
        }
      }
    }
  }
`;
